import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import settingsService, { type PluginSettings } from '../services/api/settings.service';

interface ThemeContextType {
  colors: {
    primary: string;
    secondary: string;
    success: string;
    error: string;
  };
  settings: PluginSettings | null;
  isLoading: boolean;
  refreshTheme: () => Promise<void>;
}

const ThemeContext = createContext<ThemeContextType>({
  colors: {
    primary: '#ff7a00',
    secondary: '#667eea',
    success: '#10b981',
    error: '#ef4444',
  },
  settings: null,
  isLoading: true,
  refreshTheme: async () => {},
});

export const useTheme = () => useContext(ThemeContext);

interface ThemeProviderProps {
  children: ReactNode;
}

/**
 * Theme Provider
 * Fetches brand colors from backend and applies them as CSS variables
 */
export function ThemeProvider({ children }: ThemeProviderProps) {
  const [settings, setSettings] = useState<PluginSettings | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    loadSettings();
    
    // Add keyboard shortcut: Ctrl+Shift+T to refresh theme
    const handleKeyPress = (e: KeyboardEvent) => {
      if (e.ctrlKey && e.shiftKey && e.key === 'T') {

        loadSettings(true);
      }
    };
    
    window.addEventListener('keydown', handleKeyPress);
    
    // Cleanup
    return () => {
      window.removeEventListener('keydown', handleKeyPress);
    };
  }, []);

  const loadSettings = async (forceRefresh = false) => {
    try {

      const fetchedSettings = await settingsService.getSettings(forceRefresh);

      setSettings(fetchedSettings);
      
      // Apply CSS variables to root
      applyThemeColors(fetchedSettings);

    } catch (error) {

      // Use defaults
      const defaults = {
        booking_display_mode: 'modal' as const,
        primary_color: '#ff7a00',
        secondary_color: '#667eea',
        success_color: '#10b981',
        error_color: '#ef4444',
        hold_minutes: 15,
        default_duration: 30,
      };
      setSettings(defaults);
      applyThemeColors(defaults);
    } finally {
      setIsLoading(false);
    }
  };

  const applyThemeColors = (settings: PluginSettings) => {
    const root = document.documentElement;
    // Set CSS custom properties
    root.style.setProperty('--color-primary', settings.primary_color);
    root.style.setProperty('--color-secondary', settings.secondary_color);
    root.style.setProperty('--color-success', settings.success_color);
    root.style.setProperty('--color-error', settings.error_color);
    
    // Calculate lighter/darker variants
    const primaryLight = lightenColor(settings.primary_color, 20);
    const primaryDark = darkenColor(settings.primary_color, 20);
    const secondaryLight = lightenColor(settings.secondary_color, 20);
    const secondaryDark = darkenColor(settings.secondary_color, 20);
    
    root.style.setProperty('--color-primary-light', primaryLight);
    root.style.setProperty('--color-primary-dark', primaryDark);
    root.style.setProperty('--color-secondary-light', secondaryLight);
    root.style.setProperty('--color-secondary-dark', secondaryDark);
    // RGB variants for opacity usage
    root.style.setProperty('--color-primary-rgb', hexToRgb(settings.primary_color));
    root.style.setProperty('--color-secondary-rgb', hexToRgb(settings.secondary_color));
  };

  const colors = {
    primary: settings?.primary_color || '#ff7a00',
    secondary: settings?.secondary_color || '#667eea',
    success: settings?.success_color || '#10b981',
    error: settings?.error_color || '#ef4444',
  };

  const refreshTheme = async () => {

    await loadSettings(true);
  };

  return (
    <ThemeContext.Provider value={{ colors, settings, isLoading, refreshTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}

/**
 * Helper: Convert hex to RGB
 */
function hexToRgb(hex: string): string {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!result) return '0, 0, 0';
  
  const r = parseInt(result[1], 16);
  const g = parseInt(result[2], 16);
  const b = parseInt(result[3], 16);
  
  return `${r}, ${g}, ${b}`;
}

/**
 * Helper: Lighten color
 */
function lightenColor(hex: string, percent: number): string {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!result) return hex;
  
  let r = parseInt(result[1], 16);
  let g = parseInt(result[2], 16);
  let b = parseInt(result[3], 16);
  
  r = Math.min(255, Math.floor(r + (255 - r) * (percent / 100)));
  g = Math.min(255, Math.floor(g + (255 - g) * (percent / 100)));
  b = Math.min(255, Math.floor(b + (255 - b) * (percent / 100)));
  
  return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
}

/**
 * Helper: Darken color
 */
function darkenColor(hex: string, percent: number): string {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!result) return hex;
  
  let r = parseInt(result[1], 16);
  let g = parseInt(result[2], 16);
  let b = parseInt(result[3], 16);
  
  r = Math.max(0, Math.floor(r * (1 - percent / 100)));
  g = Math.max(0, Math.floor(g * (1 - percent / 100)));
  b = Math.max(0, Math.floor(b * (1 - percent / 100)));
  
  return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
}
